import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Gamepad2, Target, Sparkles, FileText, ArrowRight, Zap, Users, Trophy, Mail, Phone } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animated counter component
const AnimatedCounter = ({ value }) => {
    const [displayValue, setDisplayValue] = useState('0');
    const ref = useRef(null);
    const [hasAnimated, setHasAnimated] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasAnimated) {
                    setHasAnimated(true);
                    // Parse and animate the value
                    const isPercentage = value.includes('%');
                    const hasK = value.includes('K');
                    const hasM = value.includes('M');
                    const hasPlus = value.includes('+');
                    
                    let numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
                    
                    const duration = 2000;
                    const steps = 60;
                    const stepDuration = duration / steps;
                    let currentStep = 0;

                    const interval = setInterval(() => {
                        currentStep++;
                        const progress = currentStep / steps;
                        const easeOut = 1 - Math.pow(1 - progress, 3);
                        const currentValue = numericValue * easeOut;

                        let formatted;
                        if (hasM) {
                            formatted = currentValue.toFixed(1) + 'M';
                        } else if (hasK) {
                            formatted = currentValue.toFixed(1) + 'K';
                        } else if (isPercentage) {
                            formatted = Math.round(currentValue) + '%';
                        } else {
                            formatted = Math.round(currentValue).toString();
                        }
                        
                        if (hasPlus && !isPercentage) {
                            formatted += '+';
                        }

                        setDisplayValue(formatted);

                        if (currentStep >= steps) {
                            clearInterval(interval);
                            setDisplayValue(value);
                        }
                    }, stepDuration);
                }
            },
            { threshold: 0.5 }
        );

        const currentRef = ref.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [value, hasAnimated]);

    return (
        <div ref={ref} className="text-3xl font-bold font-mono">
            {displayValue}
        </div>
    );
};

export default function Landing() {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [stats, setStats] = useState({
        total_users: 127,
        total_resumes: 342,
        success_rate: 85
    });

    useEffect(() => {
        const loadStats = async () => {
            try {
                const response = await axios.get(`${API}/stats`);
                setStats(response.data);
            } catch (error) {
                console.error('Failed to fetch stats:', error);
                // Keep default stats on error
            }
        };
        loadStats();
    }, []);

    const formatNumber = (num) => {
        if (num >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M+';
        } else if (num >= 1000) {
            return (num / 1000).toFixed(1) + 'K+';
        }
        return num.toString() + '+';
    };

    const handleGetStarted = () => {
        if (isAuthenticated) {
            navigate('/dashboard');
        } else {
            navigate('/login');
        }
    };

    const features = [
        {
            icon: <Gamepad2 className="w-8 h-8" />,
            title: "Add Your Games",
            description: "List your favorite games across PC, Console, and Mobile platforms with your achievements and rankings.",
            image: "https://images.pexels.com/photos/9071735/pexels-photo-9071735.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
        },
        {
            icon: <Sparkles className="w-8 h-8" />,
            title: "AI Translation",
            description: "Our AI transforms your gaming skills into professional competencies that employers value.",
            image: "https://images.unsplash.com/photo-1655711789589-b6367b970712?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxmdXR1cmlzdGljJTIwZGlnaXRhbCUyMGludGVyZmFjZSUyMGh1ZHxlbnwwfHx8fDE3NzIwNzUyMDJ8MA&ixlib=rb-4.1.0&q=85"
        },
        {
            icon: <FileText className="w-8 h-8" />,
            title: "Export Resume",
            description: "Download your professional resume as a polished PDF ready for job applications.",
            image: "https://images.pexels.com/photos/3165335/pexels-photo-3165335.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
        }
    ];

    const statsDisplay = [
        { icon: <Users className="w-6 h-6" />, value: formatNumber(stats.total_users), label: "Gamers Leveled Up" },
        { icon: <Trophy className="w-6 h-6" />, value: formatNumber(stats.total_resumes), label: "Resumes Created" },
        { icon: <Zap className="w-6 h-6" />, value: `${stats.success_rate}%`, label: "Got Interviews*", tooltip: true }
    ];

    return (
        <div className="min-h-screen bg-[#09090b] scanlines">
            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center hero-gradient">
                <img 
                    src="https://images.unsplash.com/photo-1758410473619-c204b1bcf13a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxlc3BvcnRzJTIwZ2FtaW5nJTIwc2V0dXAlMjBuZW9ufGVufDB8fHx8MTc3MjA3NTIwMXww&ixlib=rb-4.1.0&q=85"
                    alt="Gaming Setup"
                    className="absolute inset-0 w-full h-full object-cover opacity-20"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#09090b]/50 to-[#09090b]" />
                
                <div className="relative z-10 w-full px-6 md:px-12 lg:px-24 py-32">
                    <div className="max-w-4xl">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-sm mb-8 animate-fadeIn">
                            <Zap className="w-4 h-4 text-primary" />
                            <span className="text-sm font-mono text-primary tracking-wider">TRANSFORM YOUR GAMING INTO CAREER</span>
                        </div>
                        
                        <h1 className="text-5xl md:text-7xl font-bold tracking-tight uppercase mb-6 animate-fadeIn stagger-1" style={{ opacity: 0 }}>
                            Level Up<br />
                            <span className="text-primary">Your Resume</span>
                        </h1>
                        
                        {/* Catchy Quote */}
                        <p className="text-xl md:text-2xl text-foreground italic mb-4 animate-fadeIn stagger-2" style={{ opacity: 0 }}>
                            "Remember when gaming was 'a waste of time'? Yeah… about that."
                        </p>
                        
                        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mb-10 animate-fadeIn stagger-2" style={{ opacity: 0 }}>
                            We translate your gaming skills into real-world job experience. Our AI analyzes your gameplay and creates a resume that speaks to employers.
                        </p>
                        
                        <div className="flex flex-col sm:flex-row gap-4 animate-fadeIn stagger-3" style={{ opacity: 0 }}>
                            <Button 
                                data-testid="get-started-btn"
                                onClick={handleGetStarted}
                                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider px-8 py-6 text-lg btn-glow transition-all"
                            >
                                Get Started <ArrowRight className="ml-2 w-5 h-5" />
                            </Button>
                            <Button 
                                data-testid="learn-more-btn"
                                variant="outline"
                                onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
                                className="border-white/20 hover:bg-white/5 rounded-sm font-bold uppercase tracking-wider px-8 py-6 text-lg"
                            >
                                Learn More
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 px-6 md:px-12 lg:px-24 border-y border-border">
                <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
                    {statsDisplay.map((stat, index) => (
                        <div 
                            key={`stat-${stat.label}`} 
                            className="flex items-center gap-4 p-6 glass rounded-lg animate-fadeIn"
                            style={{ animationDelay: `${index * 0.1}s`, opacity: 0 }}
                        >
                            <div className="p-3 bg-primary/20 rounded-lg text-primary">
                                {stat.icon}
                            </div>
                            <div>
                                <AnimatedCounter value={stat.value} />
                                <div className="text-sm text-muted-foreground">{stat.label}</div>
                            </div>
                        </div>
                    ))}
                </div>
                <p className="text-xs text-muted-foreground text-center mt-6 max-w-2xl mx-auto">
                    *Based on early user surveys. {stats.success_rate}% of respondents reported receiving at least one interview after using their LevelUp resume.
                </p>
            </section>

            {/* Features Section */}
            <section id="features" className="py-24 px-6 md:px-12 lg:px-24">
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-4">
                            How It Works
                        </h2>
                        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
                            Three simple steps to transform your gaming experience into career-ready skills
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {features.map((feature, index) => (
                            <div 
                                key={`feature-${feature.title}`}
                                className="feature-card bg-card border border-border rounded-lg overflow-hidden animate-fadeIn"
                                style={{ animationDelay: `${index * 0.15}s`, opacity: 0 }}
                            >
                                <div className="h-48 relative overflow-hidden">
                                    <img 
                                        src={feature.image} 
                                        alt={feature.title}
                                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                                    <div className="absolute bottom-4 left-4 p-3 bg-primary/20 backdrop-blur-sm rounded-lg text-primary">
                                        {feature.icon}
                                    </div>
                                </div>
                                <div className="p-6">
                                    <div className="text-sm font-mono text-primary mb-2">STEP {index + 1}</div>
                                    <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                                    <p className="text-muted-foreground">{feature.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 px-6 md:px-12 lg:px-24 hero-gradient">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">
                        Ready to <span className="text-primary">Level Up</span>?
                    </h2>
                    <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
                        Join thousands of gamers who have successfully translated their skills into careers.
                    </p>
                    <Button 
                        data-testid="cta-create-resume-btn"
                        onClick={handleGetStarted}
                        className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider px-10 py-6 text-lg btn-glow transition-all animate-pulse-glow"
                    >
                        Create Your Resume <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-6 md:px-12 lg:px-24 border-t border-border">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                        {/* Brand */}
                        <div>
                            <div className="flex items-center gap-2 mb-4">
                                <Target className="w-6 h-6 text-primary" />
                                <span className="font-bold text-lg tracking-tight">LevelUp Resume</span>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Transform your gaming achievements into professional career opportunities.
                            </p>
                        </div>
                        
                        {/* Quick Links */}
                        <div>
                            <h4 className="font-bold mb-4">Quick Links</h4>
                            <div className="flex flex-col gap-2">
                                <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    Pricing
                                </Link>
                                <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    Login
                                </Link>
                                <Link to="/register" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                                    Sign Up
                                </Link>
                            </div>
                        </div>
                        
                        {/* Customer Service */}
                        <div>
                            <h4 className="font-bold mb-4">Customer Service</h4>
                            <div className="flex flex-col gap-2">
                                <a 
                                    href="mailto:alexhansen2701@gmail.com" 
                                    className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                                >
                                    <Mail className="w-4 h-4" />
                                    alexhansen2701@gmail.com
                                </a>
                                <a 
                                    href="tel:+61469305935" 
                                    className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-2"
                                >
                                    <Phone className="w-4 h-4" />
                                    +61 469 305 935
                                </a>
                            </div>
                        </div>
                    </div>
                    
                    <div className="pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
                        <p className="text-sm text-muted-foreground">
                            © 2024 LevelUp Resume. All rights reserved.
                        </p>
                        <p className="text-sm text-muted-foreground">
                            Made for gamers, by gamers.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
